const SITUATION_KEY = "situation";
const BACKGROUND_KEY = "background";

export const getInitialStep = (filing) => {
  if (isTaxService(filing)) {
    return 0; // Tax Type step
  } else if (isTaxSituation(filing)) {
    return 1; // Tax Situation step
  } else if (isTaxBackground(filing)) {
    return 2; // Tax Background step
  } else if (isTaxPayment(filing)) {
    return 3; // Tax Price step
  } else {
    return 0; // If not match lets initialize to step 0
  }
};

const isTaxService = (filing) => {
  if (!filing.tax_service || filing.tax_service?.services.length === 0) {
    return true;
  }
  return false;
};

const isTaxSituation = (filing) => {
  // If payment has been accepted, all previous steps including situation are complete
  if (filing.payment_accepted === true) {
    return false;
  }
  
  // If background exists, tax situation must be complete (user has moved past it)
  const services = filing.tax_service?.services || [];
  const details = filing.tax_situation?.details || {};
  
  // Check if any service has background data - if so, tax situation is complete
  let hasBackgroundData = false;
  for (const service of services) {
    if (service in details && BACKGROUND_KEY in details[service]) {
      const background = details[service][BACKGROUND_KEY];
      // If background has meaningful data (not just metadata), tax situation is complete
      if (background && Object.keys(background).length > 0) {
        const dataKeys = Object.keys(background).filter(
          key => key !== "serviceType" && 
                 key !== "_lastCompletedSectionIndex" && 
                 key !== "_isBackgroundComplete"
        );
        if (dataKeys.length > 0) {
          // Background has data, so tax situation is complete
          hasBackgroundData = true;
          break;
        }
      }
    }
  }
  
  // If any service has background data, tax situation is complete
  if (hasBackgroundData) {
    return false;
  }
  
  if (
    !filing.tax_situation?.details ||
    Object.keys(filing.tax_situation.details).length === 0
  ) {
    // If details is not defined yet, or is empty, then tax situation have not been set
    return true;
  }
  
  // If no services defined, stay on tax-situation step
  if (services.length === 0) {
    return true;
  }
  
  // Check each service: if any service is missing situation OR if situation exists but is incomplete, stay on tax-situation
  for (const service of services) {
    if (!(service in details)) {
      // Service not in details at all - stay on tax-situation
      return true;
    }
    
    if (!(SITUATION_KEY in details[service])) {
      // Service exists but missing situation key - stay on tax-situation
      return true;
    }
    
    const situation = details[service][SITUATION_KEY];
    if (!situation || Object.keys(situation).length === 0) {
      // Situation is empty - stay on tax-situation
      return true;
    }
    
    // Filter out serviceType and check if we have meaningful data
    const dataKeys = Object.keys(situation).filter(key => key !== "serviceType");
    if (dataKeys.length === 0) {
      // Only serviceType exists, no actual data - stay on tax-situation
      return true;
    }
    
    // Check for required fields from the FIRST section (Filing_Basics/Filing_Basic)
    // These must be present for the step to be considered started
    if (service === "PTF") {
      const hasTaxYears = situation.taxYears && (
        Array.isArray(situation.taxYears) ? situation.taxYears.length > 0 : situation.taxYears
      );
      const hasIncomeStates = situation.incomeStates && (
        Array.isArray(situation.incomeStates) ? situation.incomeStates.length > 0 : situation.incomeStates
      );
      const hasFilingStatus = situation.filingStatus && situation.filingStatus !== "";
      
      // If required fields from first section are missing, stay on tax-situation
      if (!hasTaxYears || !hasIncomeStates || !hasFilingStatus) {
        return true;
      }
      
      // Check if user has reached the last section (Additional_Notes)
      // The description field is optional, but if the user has navigated through all sections,
      // they would have at least seen this field. However, since it's optional, we can't rely on it.
      // Instead, check if user has interacted with fields from multiple sections
      // A simple heuristic: if they have data beyond just the first section's required fields,
      // they've likely progressed further. But to be safe, we'll check for a field from a later section.
      
      // Check if user has interacted with Source_of_Income section (second section)
      // This section has validation: atLeastOneTrue, so at least one field should be true if completed
      const hasSourceOfIncome = 
        situation.wtwoForm?.value === true ||
        situation.tenForm?.value === true ||
        situation.rentalProperties?.value === true ||
        situation.stockOrDividends?.value === true ||
        situation.scheduleK?.value === true ||
        situation.cryptoTransaction?.value === true ||
        situation.llc_owner?.value === true;
      
      // If they haven't interacted with Source_of_Income section, they're still in progress
      // But wait - this section has "atLeastOneTrue" validation, so they might have skipped it
      // Actually, the validation requires at least one to be true, so if none are true, they haven't completed it
      // However, if the section is optional or the validation allows all false, this won't work
      
      // Better approach: Check if background_info exists - if it does, they've moved past tax-situation
      // If background_info doesn't exist, they're still on tax-situation or haven't completed it
      // Actually, that's what isTaxBackground checks for
      
      // Check if user has completed all sections by checking the stored section index
      // PTF Tax_Basics has 6 sections (indices 0-5): 
      // 0: Filing_Basics, 1: Source_of_Income, 2: Health_Insurance, 
      // 3: Credits & Deductions, 4: Other_Situations, 5: Additional_Notes
      const lastCompletedIndex = situation._lastCompletedSectionIndex;
      const totalSections = 6; // PTF Tax_Basics has 6 sections
      const lastSectionIndex = totalSections - 1; // Index 5 is the last section
      
      // If _isTaxSituationComplete flag is set, they've completed it
      if (situation._isTaxSituationComplete === true) {
        // They've completed all sections - tax-situation step is complete
        return false;
      }
      
      // If lastCompletedIndex is not set or is less than the last section index, they're still in progress
      if (lastCompletedIndex === undefined || lastCompletedIndex === null || lastCompletedIndex < lastSectionIndex) {
        // Haven't reached the last section - stay on tax-situation
        return true;
      }
    } else if (service === "BTF") {
      const hasTaxYears = situation.taxYears && (
        Array.isArray(situation.taxYears) ? situation.taxYears.length > 0 : situation.taxYears
      );
      const hasFilingStatus = situation.filingStatus && situation.filingStatus !== "";
      
      // If required fields from first section are missing, stay on tax-situation
      if (!hasTaxYears || !hasFilingStatus) {
        return true;
      }
      
      // Check if user has completed all sections by checking the stored section index
      // BTF Tax_Basics has 2 sections (indices 0-1):
      // 0: Filing_Basic, 1: Additional_Notes
      const lastCompletedIndex = situation._lastCompletedSectionIndex;
      const totalSections = 2; // BTF Tax_Basics has 2 sections
      const lastSectionIndex = totalSections - 1; // Index 1 is the last section
      
      // If _isTaxSituationComplete flag is set, they've completed it
      if (situation._isTaxSituationComplete === true) {
        // They've completed all sections - tax-situation step is complete
        return false;
      }
      
      // If lastCompletedIndex is not set or is less than the last section index, they're still in progress
      if (lastCompletedIndex === undefined || lastCompletedIndex === null || lastCompletedIndex < lastSectionIndex) {
        // Haven't reached the last section - stay on tax-situation
        return true;
      }
    }
  }
  
  // All services have complete situation data - tax-situation step is complete
  return false;
};

const isTaxBackground = (filing) => {
  // If payment has been accepted, all previous steps including background are complete
  if (filing.payment_accepted === true) {
    return false;
  }
  
  if (
    !filing.tax_situation?.details ||
    Object.keys(filing.tax_situation.details).length === 0
  ) {
    // If details is not defined yet, or is empty, then background has not been set
    return true;
  }
  
  // Check if all services have complete background data
  const services = filing.tax_service?.services || [];
  const details = filing.tax_situation.details;
  
  // If no services defined, stay on tax-background step
  if (services.length === 0) {
    return true;
  }
  
  // Check each service: if any service is missing background OR if background exists but is incomplete, stay on tax-background
  for (const service of services) {
    if (!(service in details)) {
      // Service not in details at all - stay on tax-background
      return true;
    }
    
    if (!(BACKGROUND_KEY in details[service])) {
      // Service exists but missing background key - stay on tax-background
      return true;
    }
    
    const background = details[service][BACKGROUND_KEY];
    if (!background || Object.keys(background).length === 0) {
      // Background is empty - stay on tax-background
      return true;
    }
    
    // Filter out serviceType and metadata keys, check if we have meaningful data
    const dataKeys = Object.keys(background).filter(
      key => key !== "serviceType" && 
             key !== "_lastCompletedSectionIndex" && 
             key !== "_isBackgroundComplete"
    );
    if (dataKeys.length === 0) {
      // Only serviceType/metadata exists, no actual data - stay on tax-background
      return true;
    }
    
    // Check for completion metadata
    // PTF Background has 2 sections (indices 0-1): Personal Information, Addresses
    // BTF Background has 3 sections (indices 0-2): Basic Business Information, Business Address, Ownership & Structure
    const lastCompletedIndex = background._lastCompletedSectionIndex;
    const totalSections = service === "PTF" ? 2 : 3;
    const lastSectionIndex = totalSections - 1;
    
    // If _isBackgroundComplete flag is set, they've completed it
    if (background._isBackgroundComplete === true) {
      // They've completed all sections - tax-background step is complete
      continue; // Check next service
    }
    
    // Check for required fields from the FIRST section FIRST
    // We need to ensure all required fields are present before considering completion
    // Just reaching the last section doesn't mean completion - all required fields must be present
    if (service === "PTF") {
      // PTF Background first section: Personal Information
      const hasFirstName = background.firstname && background.firstname !== "";
      const hasLastName = background.lastname && background.lastname !== "";
      const hasDateOfBirth = background.dBirth && background.dBirth !== "";
      const hasEmail = background.email && background.email !== "";
      const hasPhone = background.phone && background.phone !== "";
      const hasOccupation = background.occupation && background.occupation !== "";
      
      // If required fields from first section are missing, stay on tax-background
      if (!hasFirstName || !hasLastName || !hasDateOfBirth || !hasEmail || !hasPhone || !hasOccupation) {
        return true;
      }
      
      // Check if last section (Addresses) has data - if so, they've completed it
      const hasAddresses = background.addresses && Array.isArray(background.addresses) && background.addresses.length > 0;
      
      // If all required fields are present AND (they've reached the last section OR addresses exist), consider it complete
      if ((lastCompletedIndex !== undefined && lastCompletedIndex !== null && lastCompletedIndex >= lastSectionIndex) || hasAddresses) {
        // They've completed all sections with all required fields - tax-background step is complete
        continue; // Check next service
      }
    } else if (service === "BTF") {
      // BTF Background first section: Basic Business Information
      const hasBusinessName = background.businessName && background.businessName !== "";
      const hasYearEstablished = background.yearEstablished && background.yearEstablished !== "";
      const hasStateRegistration = background.stateRegistration && background.stateRegistration !== "";
      
      // If required fields from first section are missing, stay on tax-background
      if (!hasBusinessName || !hasYearEstablished || !hasStateRegistration) {
        return true;
      }
      
      // Check if last section (Ownership & Structure) has data - if so, they've completed it
      // Last section fields: ownersNumber, ownershipChanges, distributionDividendsIssued
      const hasOwnershipData = (background.ownersNumber && background.ownersNumber !== "") ||
                               (background.ownershipChanges && background.ownershipChanges.value !== null) ||
                               (background.distributionDividendsIssued && background.distributionDividendsIssued.value !== null);
      
      // Check if businessAddress exists (section 1) - required for completion
      const hasBusinessAddress = background.businessAddress && typeof background.businessAddress === "object" && 
                                 (background.businessAddress.address || background.businessAddress.city);
      
      // For BTF, we need: section 0 (required fields) + section 1 (businessAddress) + section 2 (ownership data OR reached last section)
      // If they've reached the last section index, they've completed it
      // OR if they have ownership data, they've filled the last section
      // But we also need businessAddress (section 1) to be present
      if (hasBusinessAddress && 
          ((lastCompletedIndex !== undefined && lastCompletedIndex !== null && lastCompletedIndex >= lastSectionIndex) || 
           hasOwnershipData)) {
        // They've completed all sections with all required fields - tax-background step is complete
        continue; // Check next service
      }
      
      // If businessAddress is missing, they haven't completed section 1 - stay on background
      if (!hasBusinessAddress) {
        return true;
      }
    }
    
    // If we get here, required fields might be present but they haven't reached the last section yet
    // OR lastCompletedIndex is not set - stay on tax-background
    if (lastCompletedIndex === undefined || lastCompletedIndex === null || lastCompletedIndex < lastSectionIndex) {
      return true;
    }
  }
  
  // All services have complete background data - tax-background step is complete
  return false;
};

const isTaxPayment = (filing) => {
  // If payment has been accepted, payment step is complete
  if (filing.payment_accepted === true) {
    return false;
  }
  
  // If filing status indicates payment is complete or in progress, stay on payment step
  // This handles the case where payment intent is created but payment hasn't been accepted yet
  // Statuses: "ACT" (Active), "PRO" (Processing), "COM" (Complete) indicate payment is done
  // But if payment_accepted is false, we should still show payment step
  // Actually, if status is ACT/PRO/COM, we redirect in the wizard, so we don't need to check here
  
  // If background is not complete, stay on payment step
  // This is the inverse of isTaxBackground - if background is incomplete, we need payment step
  // If background is complete AND payment hasn't been accepted, we should stay on payment step
  
  // Check if all services have complete background data
  if (
    !filing.tax_situation?.details ||
    Object.keys(filing.tax_situation.details).length === 0
  ) {
    // If details is not defined yet, or is empty, then background has not been set
    return true;
  }
  
  const services = filing.tax_service?.services || [];
  const details = filing.tax_situation.details;
  
  // If no services defined, stay on payment step
  if (services.length === 0) {
    return true;
  }
  
  // Check if all services have complete background data
  let allBackgroundsComplete = true;
  
  // Check each service: if any service is missing background OR if background exists but is incomplete, stay on payment step
  for (const service of services) {
    if (!(service in details)) {
      // Service not in details at all - stay on payment step
      return true;
    }
    
    if (!(BACKGROUND_KEY in details[service])) {
      // Service exists but missing background key - stay on payment step
      return true;
    }
    
    const background = details[service][BACKGROUND_KEY];
    if (!background || Object.keys(background).length === 0) {
      // Background is empty - stay on payment step
      return true;
    }
    
    // Filter out serviceType and metadata keys, check if we have meaningful data
    const dataKeys = Object.keys(background).filter(
      key => key !== "serviceType" && 
             key !== "_lastCompletedSectionIndex" && 
             key !== "_isBackgroundComplete"
    );
    if (dataKeys.length === 0) {
      // Only serviceType/metadata exists, no actual data - stay on payment step
      return true;
    }
    
    // Check for completion metadata
    const lastCompletedIndex = background._lastCompletedSectionIndex;
    const totalSections = service === "PTF" ? 2 : 3;
    const lastSectionIndex = totalSections - 1;
    
    // If _isBackgroundComplete flag is set, they've completed it
    if (background._isBackgroundComplete === true) {
      continue; // Check next service
    }
    
    // If lastCompletedIndex is not set or is less than the last section index, they're still in progress
    if (lastCompletedIndex === undefined || lastCompletedIndex === null || lastCompletedIndex < lastSectionIndex) {
      // Haven't reached the last section - stay on payment step
      return true;
    }
    
    // Check for required fields from the FIRST section
    if (service === "PTF") {
      const hasFirstName = background.firstname && background.firstname !== "";
      const hasLastName = background.lastname && background.lastname !== "";
      const hasDateOfBirth = background.dBirth && background.dBirth !== "";
      const hasEmail = background.email && background.email !== "";
      const hasPhone = background.phone && background.phone !== "";
      const hasOccupation = background.occupation && background.occupation !== "";
      
      // If required fields from first section are missing, stay on payment step
      if (!hasFirstName || !hasLastName || !hasDateOfBirth || !hasEmail || !hasPhone || !hasOccupation) {
        return true;
      }
    } else if (service === "BTF") {
      const hasBusinessName = background.businessName && background.businessName !== "";
      const hasYearEstablished = background.yearEstablished && background.yearEstablished !== "";
      const hasStateRegistration = background.stateRegistration && background.stateRegistration !== "";
      
      // If required fields from first section are missing, stay on payment step
      if (!hasBusinessName || !hasYearEstablished || !hasStateRegistration) {
        return true;
      }
    }
    
    // If we get here for this service, background is complete
    allBackgroundsComplete = allBackgroundsComplete && true;
  }
  
  // If all backgrounds are complete but payment hasn't been accepted, stay on payment step
  // This ensures that after refresh, if user is on payment step, they stay there
  if (allBackgroundsComplete && !filing.payment_accepted) {
    return true; // Stay on payment step
  }
  
  // All services have complete background data AND payment has been accepted - payment step is complete
  return false;
};

const serviceHelper = (filing, key) => {
  const services = filing.tax_service.services;
  const details = filing.tax_situation.details;

  for (const service of services) {
    if (service in details && !(key in details[service])) {
      return true;
    }
  }
  return false;
};
